import { API_CONFIG } from '../../config/api.config'

const BASE = `${API_CONFIG.python.apiUrl}/collaboration`

function getToken(): string {
  return (
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    ''
  )
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers || {}),
    },
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${detail}`)
  }
  return res.json()
}

export interface GroupMember {
  id: string
  group_id: string
  student_email: string
  student_name: string | null
  role: string
  joined_at: string
}

export interface CollaborativeGroup {
  id: string
  group_name: string
  class_name: string | null
  teacher_id: string
  created_at: string
  updated_at: string
  members: GroupMember[]
}

export interface GroupTask {
  id: string
  title: string
  description: string | null
  class_name: string
  lesson_id?: string | null
  teacher_id: string
  due_date: string | null
  created_at: string
}

export interface GroupSubmission {
  id: string
  task_id: string
  group_id: string
  file_url: string | null
  content: string | null
  submitted_by: string
  submitted_at: string
  teacher_score?: number | null
  teacher_feedback?: string | null
  graded_at?: string | null
}

export interface PeerReview {
  id: string
  submission_id: string
  reviewer_email: string
  score: number
  feedback: string | null
  reviewed_at: string
}

export const collaborationService = {
  // Teacher groups
  async listTeacherGroups(className?: string): Promise<CollaborativeGroup[]> {
    const path = className ? `/teacher/groups?class_name=${encodeURIComponent(className)}` : '/teacher/groups'
    return req<CollaborativeGroup[]>(path)
  },
  async createGroup(data: { group_name: string; class_name?: string; members: { student_email: string; student_name?: string; role?: string }[] }): Promise<CollaborativeGroup> {
    return req<CollaborativeGroup>('/teacher/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async updateGroup(groupId: string, data: { group_name: string; members: { student_email: string; student_name?: string; role?: string }[] }): Promise<CollaborativeGroup> {
    return req<CollaborativeGroup>(`/teacher/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },
  async deleteGroup(groupId: string): Promise<{ success: boolean; message: string }> {
    return req<{ success: boolean; message: string }>(`/teacher/groups/${groupId}`, {
      method: 'DELETE',
    })
  },

  // Student groups
  async listMyGroups(): Promise<CollaborativeGroup[]> {
    return req<CollaborativeGroup[]>('/student/my-groups')
  },

  // Danh sách lớp (roster) để CHỌN thành viên khi tạo nhóm (#1)
  async listRoster(className?: string): Promise<{ student_email: string; student_name: string; class_name: string }[]> {
    const path = className ? `/teacher/roster?class_name=${encodeURIComponent(className)}` : '/teacher/roster'
    return req(path)
  },

  // Upload FILE sản phẩm nhóm (giới hạn dung lượng ở backend) → trả url (#2)
  async uploadFile(file: File): Promise<{ url: string }> {
    const fd = new FormData()
    fd.append('file', file)
    const res = await fetch(`${BASE}/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: fd,
    })
    if (!res.ok) throw new Error(`API ${res.status}: ${await res.text().catch(() => '')}`)
    return res.json()
  },

  // Tasks
  async createTask(data: { title: string; description?: string; class_name: string; lesson_id?: string; due_date?: string }): Promise<GroupTask> {
    return req<GroupTask>('/teacher/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async updateTask(taskId: string, data: { title: string; description?: string; class_name: string; lesson_id?: string; due_date?: string }): Promise<GroupTask> {
    return req<GroupTask>(`/teacher/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify(data) })
  },
  // GV chấm điểm nhóm (thang /10) (#3)
  async gradeSubmission(data: { submission_id: string; score: number; feedback?: string }): Promise<GroupSubmission> {
    return req<GroupSubmission>('/teacher/grade', { method: 'POST', body: JSON.stringify(data) })
  },
  async deleteTask(taskId: string): Promise<{ success: boolean; message: string }> {
    return req(`/teacher/tasks/${taskId}`, { method: 'DELETE' })
  },
  async listTasks(className?: string): Promise<GroupTask[]> {
    const path = className ? `/tasks?class_name=${encodeURIComponent(className)}` : '/tasks'
    return req<GroupTask[]>(path)
  },
  // Nhiệm vụ nhóm gắn vào 1 bài học (trình xem bài hiển thị banner)
  async tasksByLesson(lessonId: string): Promise<GroupTask[]> {
    try { return await req<GroupTask[]>(`/tasks/by-lesson/${lessonId}`) } catch { return [] }
  },

  // Submissions
  async submitWork(data: { task_id: string; group_id: string; file_url?: string; content?: string }): Promise<GroupSubmission> {
    return req<GroupSubmission>('/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async getSubmissions(taskId: string): Promise<GroupSubmission[]> {
    return req<GroupSubmission[]>(`/submissions/${taskId}`)
  },
  // #6: sản phẩm nhóm mà HS được phân đánh giá chéo (round-robin), null nếu chưa đủ nhóm
  async reviewTarget(taskId: string): Promise<GroupSubmission | null> {
    try { return await req<GroupSubmission | null>(`/review-target/${taskId}`) } catch { return null }
  },

  // Reviews
  async submitReview(data: { submission_id: string; score: number; feedback?: string }): Promise<PeerReview> {
    return req<PeerReview>('/reviews', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async getReviews(submissionId: string): Promise<PeerReview[]> {
    return req<PeerReview[]>(`/reviews/${submissionId}`)
  },
}
